import { useCallback, useEffect, useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import axios from "axios";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Bell } from "lucide-react";
import { toast } from "sonner";

import { api_endpoint } from "@/constants/constant";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../@/components/ui/tabs";
import { Button } from "../../@/components/ui/button";
import { Skeleton } from "../../@/components/ui/skeleton";

import Pagination from "../components/pagination";
import { EmptyState } from "../components/emptyState";
import { getUserTimezone } from "../lib/timezone";

const NotificationSkeleton = () => (
  <div className="py-3 border-b border-(--neutral-200)">
    <Skeleton className="h-4 w-1/2 mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
    <div className="flex justify-between">
      <Skeleton className="h-3 w-1/4" />
      <Skeleton className="h-3 w-1/4" />
    </div>
  </div>
);

const NotificationItem = ({ notification, onClick, isLast, timezone }) => {
  const notificationDate = useMemo(() => {
    if (!notification?.created_at) return null;

    // Ensure the date is interpreted as UTC
    // If the date string doesn't end with 'Z', append it to indicate UTC
    let dateStr = notification.created_at;
    if (
      !dateStr.endsWith("Z") &&
      !dateStr.includes("+") &&
      !dateStr.includes("-", 10)
    ) {
      dateStr = dateStr + "Z";
    }

    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  }, [notification?.created_at]);

  // Get timezone offset for display
  const getTimezoneLabel = (tz) => {
    try {
      const parts = tz.split("/");
      const city = parts[parts.length - 1].replace(/_/g, " ");
      const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        timeZoneName: "shortOffset",
      });
      const offset =
        fmt.formatToParts(new Date()).find((p) => p.type === "timeZoneName")
          ?.value || "";
      return `(${city} ${offset})`;
    } catch {
      return "";
    }
  };

  const timezoneLabel = timezone ? getTimezoneLabel(timezone) : "";

  return (
    <div
      className={`relative py-3 px-1 cursor-pointer transition-colors ${
        isLast ? "" : "border-b border-(--neutral-200)"
      } hover:bg-(--neutral-100)`}
      onClick={() => onClick(notification.id)}
    >
      <p className="text-sm font-semibold text-(--neutral-900)">
        {notification.title}
      </p>
      <p className="text-sm text-(--neutral-600)">{notification.message}</p>
      {notificationDate ? (
        <div className="flex justify-between text-xs font-semibold text-(--neutral-500)">
          <p>
            {formatInTimeZone(
              notificationDate,
              timezone,
              "EEEE h:mmaaa",
            ).replace(/AM|PM/, (m) => m.toLowerCase())}{" "}
            <span className="text-xs text-(--neutral-400)">
              {timezoneLabel}
            </span>
          </p>
          <p>{formatDistanceToNow(notificationDate, { addSuffix: true })}</p>
        </div>
      ) : null}
      {!notification.is_read && (
        <span className="bg-(--color-primary) w-2 h-2 absolute top-3 right-0 rounded-full" />
      )}
    </div>
  );
};

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("unread"); // all | read | unread
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotificationsCount, setTotalNotificationsCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { getToken } = useAuth();
  const { user } = useUser();

  // Get user's preferred timezone (localStorage > browser)
  const userTimezone = useMemo(() => getUserTimezone(), []);

  const fetchNotifications = useCallback(
    async (page) => {
      if (!user) return;
      try {
        setLoading(true);
        const token = await getToken();
        if (!token) return;

        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          params: {
            ...(statusFilter !== "all" ? { status: statusFilter } : {}),
            page,
            page_size: pageSize,
          },
        };

        const response = await axios.get(
          `${api_endpoint}/notification/user-notifications`,
          config,
        );

        const payload = Array.isArray(response.data?.notifications)
          ? response.data.notifications
          : [];
        setNotifications(payload);
        setTotalPages(Number(response.data?.total_pages) || 1);
        setTotalNotificationsCount(
          Number(response.data?.total_items) ||
            (Number(response.data?.total_pages) || 1) * pageSize,
        );
        setCurrentPage(Number(response.data?.page) || page);
      } catch (error) {
        console.error("Failed to fetch notifications", error);
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    },
    [user, statusFilter, getToken, pageSize],
  );

  useEffect(() => {
    document.title = "Notifications | Zindua LMS";
  }, []);

  const handleNotificationClick = async (id) => {
    const notification = notifications.find((n) => n.id === id);
    if (notification?.is_read) return;

    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, is_read: true } : notif,
      ),
    );

    try {
      const token = await getToken();
      if (!token) return;
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      await axios.post(
        `${api_endpoint}/notification/mark-as-read`,
        { notification_ids: [id] },
        config,
      );
    } catch (error) {
      console.error(`Failed to mark notification ${id} as read`, error);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === id ? { ...notif, is_read: false } : notif,
        ),
      );
      toast.error("Failed to mark notification as read.");
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    try {
      const token = await getToken();
      if (!token) return;
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const unreadNotificationIds = notifications
        .filter((n) => !n.is_read)
        .map((n) => n.id);
      if (unreadNotificationIds.length === 0) {
        toast.info("No unread notifications to mark as read.");
        return;
      }
      await axios.post(
        `${api_endpoint}/notification/mark-as-read`,
        { notification_ids: unreadNotificationIds },
        config,
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success("All notifications marked as read.");
    } catch (error) {
      console.error("Failed to mark all notifications as read", error);
      toast.error("Failed to mark all notifications as read.");
    }
  };

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications],
  );

  const filteredNotifications = useMemo(() => {
    const list = Array.isArray(notifications) ? notifications : [];
    return list
      .filter((notification) => {
        if (statusFilter === "all") return true;
        if (statusFilter === "read") return Boolean(notification.is_read);
        if (statusFilter === "unread") return !notification.is_read;
        return true;
      })
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }, [notifications, statusFilter]);

  const isToday = (someDate) => {
    const today = new Date();
    return (
      someDate.getDate() === today.getDate() &&
      someDate.getMonth() === today.getMonth() &&
      someDate.getFullYear() === today.getFullYear()
    );
  };

  const recentNotifications = useMemo(
    () =>
      filteredNotifications.filter((notification) =>
        isToday(new Date(notification.created_at)),
      ),
    [filteredNotifications],
  );
  const earlierNotifications = useMemo(
    () =>
      filteredNotifications.filter(
        (notification) => !isToday(new Date(notification.created_at)),
      ),
    [filteredNotifications],
  );

  // Fetch notifications whenever page/filter/pageSize changes
  useEffect(() => {
    if (!user) return;
    fetchNotifications(currentPage);
  }, [user, currentPage, statusFilter, pageSize, fetchNotifications]);

  return (
    <div className="flex flex-col w-full min-h-screen bg-(--surface-base) text-(--neutral-900)">
      <h1 className="">Notifications</h1>
      <p className="text-(--neutral-700)">
        Review updates, announcements, and reminders.
      </p>

      <Tabs
        value={statusFilter}
        onValueChange={(value) => {
          setStatusFilter(value);
          setCurrentPage(1);
        }}
        className="space-y-4 mt-3"
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <TabsList className="w-full md:w-auto">
            <TabsTrigger value="unread" className="w-full md:w-auto">
              Unread
            </TabsTrigger>
            <TabsTrigger value="read" className="w-full md:w-auto">
              Read
            </TabsTrigger>
            <TabsTrigger value="all" className="w-full md:w-auto">
              All
            </TabsTrigger>
          </TabsList>

          <div className="w-full md:w-auto md:ml-auto">
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size=""
                onClick={handleMarkAllAsRead}
                className="w-full md:w-auto"
              >
                Mark all as read
              </Button>
            )}
          </div>
        </div>

        {["all", "read", "unread"].map((tabValue) => (
          <TabsContent key={tabValue} value={tabValue} className="space-y-2">
            {loading ? (
              <div className="space-y-2">
                <NotificationSkeleton />
                <NotificationSkeleton />
                <NotificationSkeleton />
                <NotificationSkeleton />
                <NotificationSkeleton />
              </div>
            ) : filteredNotifications.length > 0 ? (
              <>
                {recentNotifications.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-(--neutral-700) mb-2">
                      Recent
                    </h3>
                    {recentNotifications.map((notification, index) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={handleNotificationClick}
                        isLast={index === recentNotifications.length - 1}
                        timezone={userTimezone}
                      />
                    ))}
                  </div>
                )}
                {earlierNotifications.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-(--neutral-700) mb-2">
                      Earlier
                    </h3>
                    {earlierNotifications.map((notification, index) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={handleNotificationClick}
                        isLast={index === earlierNotifications.length - 1}
                        timezone={userTimezone}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <EmptyState
                icon={Bell}
                title="No notifications"
                description="No notifications match your current filters."
              />
            )}

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[10, 20, 50]}
                onRowsPerPageChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                onPageChange={(page) => setCurrentPage(page)}
                totalRows={totalNotificationsCount}
                rowLabel="notifications"
                showSelectedCount={false}
              />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default NotificationsPage;
